import "./Devices.css";

import { useEffect, useState } from "react";
import { Auth } from "aws-amplify";

import { checkUser, getDevices, invalidateDevices, saveDevice, upsertDeviceCache } from "../../utils/utils";

export default function Devices({ setAuthenticated }) {
    const [devices, setDevices] = useState([]);
    const [edited, setEdited] = useState({});
    const [editedNames, setEditedNames] = useState({});
    const [newName, setNewName] = useState('');
    const [newValue, setNewValue] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const getToken = async () => {
        const session = await Auth.currentSession();
        return session.getIdToken().getJwtToken();
    };

    const loadDevices = async (forceRefresh = false) => {
        setLoading(true);
        setError(false);

        try {
            if (!await checkUser(setAuthenticated))
                return;

            const token = await getToken();
            const list = await getDevices(token, forceRefresh);

            setDevices(list);

            const map = {};
            const names = {};
            list.forEach(d => { map[d.Name] = d.Value; names[d.Name] = d.Name; });
            setEdited(map);
            setEditedNames(names);
        } catch (e) {
            console.error('Error loading devices', e);
            setError(true);
        }

        setLoading(false);
    };

    useEffect(() => {
        loadDevices(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refresh = async () => {
        invalidateDevices();
        await loadDevices(true);
    };

    const addDevice = async () => {
        const name = newName.trim();
        const value = newValue.trim();

        if (!name || !value) {
            alert('Name and Value are required.');
            return;
        }

        if (devices.some(d => d.Name === name)) {
            alert('A device with that name already exists.');
            return;
        }

        const token = await getToken();
        const res = await saveDevice(token, 'PUT', { name: name, value: value });

        if (!res.ok) {
            alert('Error creating device: ' + res.errorMessage);
            return;
        }

        setNewName('');
        setNewValue('');

        // Optimistically add the new device to the cached list and local state so
        // it shows up immediately everywhere (including license pages, which read
        // the cache). An immediate refetch would race the backend's eventual
        // consistency and could drop the just-created device.
        const device = { Name: name, Value: value };
        const list = upsertDeviceCache(device);

        setDevices(list);
        setEdited(prev => ({ ...prev, [name]: value }));
        setEditedNames(prev => ({ ...prev, [name]: name }));
    };

    const updateDevice = async (name) => {
        const value = (edited[name] || '').trim();
        const newName = (editedNames[name] || '').trim();

        if (!value) {
            alert('Value is required.');
            return;
        }

        if (!newName) {
            alert('Name is required.');
            return;
        }

        const renamed = newName !== name;

        if (renamed && devices.some(d => d.Name === newName)) {
            alert('A device with that name already exists.');
            return;
        }

        // Always send the current value; only include newName when it changed.
        const body = { name: name, value: value };
        if (renamed)
            body.newName = newName;

        const token = await getToken();
        const res = await saveDevice(token, 'PATCH', body);

        if (!res.ok) {
            alert('Error updating device: ' + res.errorMessage);
            return;
        }

        await refresh();
    };

    const deleteDevice = async (name) => {
        if (!window.confirm(`Delete device "${name}"? This will NOT remove it from licenses that still reference it.`))
            return;

        const token = await getToken();
        const res = await saveDevice(token, 'DELETE', { name: name });

        if (!res.ok) {
            alert('Error deleting device: ' + res.errorMessage);
            return;
        }

        await refresh();
    };

    return (
        <div id="devices-page">
            <div id="title">Restricted Devices</div>

            {error && <div className="devices-error">Could not load devices. Try refreshing.</div>}

            {loading ? <h4>Loading devices...</h4> : (
                <table id="devices-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Value</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {devices.map(device => (
                            <tr key={device.Name}>
                                <td>
                                    <input
                                        type="text"
                                        value={editedNames[device.Name] || ''}
                                        onChange={e => setEditedNames({ ...editedNames, [device.Name]: e.target.value })}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        value={edited[device.Name] || ''}
                                        onChange={e => setEdited({ ...edited, [device.Name]: e.target.value })}
                                    />
                                </td>
                                <td>
                                    <button onClick={() => updateDevice(device.Name)}>Save</button>
                                    <button className="delete-button" onClick={() => deleteDevice(device.Name)}>Delete</button>
                                </td>
                            </tr>
                        ))}
                        <tr className="new-device-row">
                            <td>
                                <input
                                    type="text"
                                    placeholder="Name"
                                    value={newName}
                                    onChange={e => setNewName(e.target.value)}
                                />
                            </td>
                            <td>
                                <input
                                    type="text"
                                    placeholder="Value"
                                    value={newValue}
                                    onChange={e => setNewValue(e.target.value)}
                                />
                            </td>
                            <td>
                                <button onClick={addDevice}>Add device</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            )}
        </div>
    );
}
